import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
    DEFAULT_LOCALE,
    resolveLocale,
    type LocaleCode,
} from '@/configs/locale.config'

type LocaleState = {
    locale: LocaleCode
}

type LocaleAction = {
    /** The user picked this, here. Written back to the account by `useLocale`. */
    setLocale: (payload: LocaleCode) => void
    /**
     * The user picked this on some other device; take it as given.
     *
     * Separate from `setLocale` so it does not write straight back to the
     * server it just came from — see `components/template/LocaleSync`.
     */
    adoptLocale: (payload: LocaleCode) => void
}

/**
 * Initial guess, used only until the account's saved preference arrives.
 *
 * Reading `navigator.languages` rather than `navigator.language` respects the
 * user's ordered list, so someone whose browser is English-first but who also
 * lists French still lands on English rather than on whichever we checked first.
 */
const guessFromBrowser = (): LocaleCode => {
    if (typeof navigator === 'undefined') {
        return DEFAULT_LOCALE
    }

    const candidates = navigator.languages?.length
        ? navigator.languages
        : [navigator.language]

    for (const candidate of candidates) {
        const resolved = resolveLocale(candidate)
        // `resolveLocale` falls back to the default, so a match is only real
        // when the candidate actually names a language we ship.
        if (resolved !== DEFAULT_LOCALE || /^en\b/i.test(candidate ?? '')) {
            return resolved
        }
    }

    return DEFAULT_LOCALE
}

export const useLocaleStore = create<LocaleState & LocaleAction>()(
    persist(
        (set) => ({
            locale: guessFromBrowser(),
            setLocale: (payload) => set(() => ({ locale: payload })),
            adoptLocale: (payload) => set(() => ({ locale: payload })),
        }),
        {
            name: 'locale',
        },
    ),
)

export const useCurrentLocale = () => useLocaleStore((state) => state.locale)
